// ============================================================================
// NOTE EDIT HISTORY MODAL
// Muestra el histórico de ediciones de una nota.
// ============================================================================
import { useState, useEffect } from 'react';
import { X, History } from 'lucide-react';
import { patientNotesApi } from '../../api/patient-notes';
import type { PatientNoteEdit } from '../../types/patient';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  noteId: string | null;
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function NoteEditHistoryModal({ isOpen, onClose, noteId }: Props) {
  const [edits, setEdits] = useState<PatientNoteEdit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !noteId) return;
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await patientNotesApi.getEditHistory(noteId);
        if (!cancelled) setEdits(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Error al cargar histórico');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, noteId]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
            <History className="h-5 w-5 text-gray-400" />
            Histórico de ediciones
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
              {error}
            </div>
          )}

          {isLoading ? (
            <p className="text-sm text-gray-400">Cargando...</p>
          ) : edits.length === 0 && !error ? (
            <p className="text-sm text-gray-500 italic py-4 text-center">
              Esta nota no tiene ediciones previas.
            </p>
          ) : (
            <ol className="space-y-4">
              {edits.map((edit, idx) => (
                <li key={edit.id} className="border-l-2 border-gray-200 pl-4 relative">
                  <span className="absolute -left-1.5 top-1 w-3 h-3 rounded-full bg-gray-300" />
                  <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                    <span className="font-medium text-gray-700">
                      {edit.editedByName || 'Usuario'}
                    </span>
                    <span>·</span>
                    <span>{formatDateTime(edit.editedAt)}</span>
                    {idx === 0 && <span className="ml-auto bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 text-[10px]">más reciente</span>}
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {edit.previousContent}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default NoteEditHistoryModal;
